import React, { useMemo } from 'react';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { FocusArea } from 'libs/end-to-end/src/focus-area';
import { Tooltip } from '@mui/material';
import cn from 'classnames';
import { SlateActionsView } from 'apps/sigma2-ui/src/copilot/components/SlateActionsView';
import { GenerateVisualizationEvent } from 'libs/end-to-end/src/visualizer';
import { SlatePoolSubject } from 'libs/end-to-end/src/copilot';
import { Paragraph } from 'apps/sigma2-ui/src/components/ui/Paragraph';
import { IconInformationSvg } from 'apps/sigma2-ui/src/components/svg/IconInformation';
import { IconCircularArrowWithDotSvg } from 'apps/sigma2-ui/src/components/svg/IconCircularArrowWithDot';
import { IconArrowDownSvg } from 'apps/sigma2-ui/src/components/svg/IconArrowDown';
import { CalibrationFraming } from 'libs/end-to-end/src/calibration-framing';
import { BasicCopilotSortType } from 'libs/end-to-end/src/copilot-utils';
import { guardExhaustive } from 'libs/end-to-end/src/generic';
import { IconArrowTopSvg } from 'apps/sigma2-ui/src/components/svg/IconArrowTop';
import { IconArrowUpSvg } from 'apps/sigma2-ui/src/components/svg/IconArrowUp';

interface SlateHeaderViewProps {
  filteredSubjectsToDisplay: SlatePoolSubject[];
  resetSlate: () => void;
  focusArea: FocusArea;
  downloadableGenerateVisualizationEvent: GenerateVisualizationEvent | null;
  isFetchingShareLink: boolean;
  onFetchShareLink: () => void;
  isShareable: boolean;
  calibrationFraming: CalibrationFraming;
  selectedSort: BasicCopilotSortType;
  setSelectedSort: (sort: BasicCopilotSortType) => void;
}

export const SlateHeaderView = (props: SlateHeaderViewProps) => {
  const language = useLanguage();
  const numberOfSubjectsString =
    props.focusArea === 'Hiring'
      ? language.hiringCopilotSlateNumberOfCandidates({
          count: props.filteredSubjectsToDisplay.length,
        })
      : language.promotionCopilotSlateNumberOfCandidates({
          count: props.filteredSubjectsToDisplay.length,
        });
  const description =
    props.focusArea === 'Hiring'
      ? language.hiringCopilotSlateYourSlateDescription({
          calibrationFraming: props.calibrationFraming,
        })
      : language.promotionCopilotSlateYourSlateDescription({
          calibrationFraming: props.calibrationFraming,
        });

  const {
    downloadableGenerateVisualizationEvent,
    isFetchingShareLink,
    onFetchShareLink,
    isShareable,
  } = props;

  const sortUi = useMemo(() => {
    if (props.selectedSort === 'highest-to-lowest') {
      return (
        <button
          onClick={() => {
            props.setSelectedSort('lowest-to-highest');
          }}
        >
          <div className="flex justify-end items-center">
            <Paragraph className="pl-2">
              {language.hiringCopilotSlateHighestToLowest()}
            </Paragraph>
            <IconArrowDownSvg svg={{ className: 'h-5 w-5 text-gray-700' }} />
          </div>
        </button>
      );
    } else if (props.selectedSort === 'lowest-to-highest') {
      return (
        <button
          onClick={() => {
            props.setSelectedSort('highest-to-lowest');
          }}
        >
          <div className="flex justify-end items-center">
            <Paragraph className="pl-2">
              {language.hiringCopilotSlateLowestToHighest()}
            </Paragraph>
            <IconArrowUpSvg svg={{ className: 'h-5 w-5 text-gray-700' }} />
          </div>
        </button>
      );
    } else {
      throw guardExhaustive(props.selectedSort);
    }
  }, [language, props]);

  return (
    <div>
      <div className="flex flex-row items-center gap-2">
        <Paragraph
          textColor="text-black"
          fontFamily="font-sans"
          className={cn('text-xl font-bold text-gray-1000')}
        >
          {language.hiringCopilotSlateYourSlate()}
        </Paragraph>

        <Tooltip placement="bottom" title={description}>
          <span
            aria-label="Your Slate Information Icon"
            className="bg-white rounded-full p-1.5"
            data-qa="yourSlateInformationIcon"
          >
            <IconInformationSvg
              svg={{ className: 'relative w-4 text-gray-600' }}
            />
          </span>
        </Tooltip>

        <div className="flex-grow self-stretch" />

        <button className="w-6" onClick={() => props.resetSlate()}>
          <Tooltip placement="bottom" title="Reset">
            <span aria-label="Reset Icon" data-qa="reset-button">
              <IconCircularArrowWithDotSvg
                svg={{ className: 'text-secondary' }}
              />
            </span>
          </Tooltip>
        </button>

        <SlateActionsView
          downloadableGenerateVisualizationEvent={
            downloadableGenerateVisualizationEvent
          }
          isFetchingShareLink={isFetchingShareLink}
          onFetchShareLink={onFetchShareLink}
          isShareable={isShareable}
        />
      </div>

      <div className="justify-between items-center">
        <div className="flex grow-0 justify-between items-start pt-4 p-1">
          <Paragraph>{numberOfSubjectsString}</Paragraph>
          {sortUi}
        </div>
      </div>
    </div>
  );
};
